import * as THREE from 'three'
import Experience from '../../Experience'
import vertexShader from './vertex.glsl'
import fragmentShader from './fragment.glsl'

/**
 * ShootingStars — 天球（半径25、StarrySkyの内側）を流れる流星。
 * ロード中から動き出し、4〜10秒のランダム間隔で1本ずつ流れる。
 *
 * パフォーマンス方針:
 * - 固定プール（3本）を使い回し、実行時のアロケーションはゼロ
 * - 位置計算は全て頂点シェーダー内（uProgress を進めるだけ）
 * - 低スペック端末・reduced-motion では生成されない/流れない
 *
 * SceneDirector が #works 表示中は spawn を止める（iframe と負荷が重なるため）。
 */
export default class ShootingStars {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.sizes = this.experience.sizes
    this.quality = this.experience.quality
    this.debug = this.experience.debug

    // 6本あるのは流星群バースト（Celestials の goldenMoon）時の同時本数のため。
    // 通常時はスポーン間隔が長いので同時に流れるのは1〜2本のまま
    this.poolSize = this.quality.tier === 'low' ? 0 : 6
    this.skyRadius = 25
    this.trailPoints = 64 // 尾が点線に見えないよう密に（6本×64点なので負荷は無視できる）
    this.enabled = this.poolSize > 0

    // 流星群バーストモード（Celestials のゾーンエフェクトから setBurst で切り替え）
    this.burstActive = false
    this.defaultColor = new THREE.Color('#fff4e0')
    this.spawnColor = this.defaultColor.clone()
    this.burstDir = new THREE.Vector3() // バースト中の出現方向（カメラに映る空へ集める）
    this.hasBurstDir = false

    this.spawnTimer = 0
    this.nextSpawnDelay = this.randomDelay()

    // update() 内で使い回すスクラッチ（毎フレームの new を避ける）
    this.scratch = {
      normal: new THREE.Vector3(),
      tangent: new THREE.Vector3(),
      helper: new THREE.Vector3()
    }

    this.pool = []
    for (let i = 0; i < this.poolSize; i++) {
      this.pool.push(this.createStar())
    }

    this.setDebug()

    if (this.poolSize > 0) {
      this.time.on('tick', () => {
        this.update()
      })
    }
  }

  randomDelay() {
    if (this.burstActive) {
      return 0.25 + Math.random() * 0.5 // バースト中: 0.25〜0.75秒
    }
    return 4 + Math.random() * 6 // 通常: 4〜10秒
  }

  /**
   * 流星群バーストの ON/OFF。
   * ON にすると指定色（省略時は金色）の流星が短い間隔で流れ続ける。
   * direction（[x,y,z] または Vector3）を渡すと、その方向のコーン内だけに出現する
   * （デフォルトカメラに映る空へ流星を集めるため）。
   */
  setBurst(active, colorHex = '#ffe08a', direction = null) {
    this.burstActive = active
    this.spawnColor.set(active ? colorHex : this.defaultColor)
    this.hasBurstDir = active && direction !== null
    if (this.hasBurstDir) {
      this.setBurstDirection(direction)
    }
    // 次のスポーンからすぐ新しい間隔を反映させる
    this.spawnTimer = 0
    this.nextSpawnDelay = this.randomDelay()
  }

  /**
   * バースト中の出現方向だけを更新する（[x,y,z] または Vector3）。
   * カメラ追従で「今見ている空」に流星を流し続ける用途（Lantern）。
   */
  setBurstDirection(direction) {
    if (Array.isArray(direction)) {
      this.burstDir.set(direction[0], direction[1], direction[2]).normalize()
    } else {
      this.burstDir.copy(direction).normalize()
    }
  }

  createStar() {
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(this.trailPoints * 3) // 実際の位置はシェーダーが決める
    const trails = new Float32Array(this.trailPoints)
    for (let i = 0; i < this.trailPoints; i++) {
      trails[i] = i / (this.trailPoints - 1)
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aTrail', new THREE.BufferAttribute(trails, 1))

    const material = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uStart: new THREE.Uniform(new THREE.Vector3()),
        uDir: new THREE.Uniform(new THREE.Vector3(1, 0, 0)),
        uProgress: new THREE.Uniform(0),
        uFlightDist: new THREE.Uniform(14),
        uTrailLength: new THREE.Uniform(5),
        uPixelRatio: new THREE.Uniform(Math.min(this.sizes.pixelRatio, this.quality.maxPixelRatio)),
        uColor: new THREE.Uniform(new THREE.Color('#fff4e0')),
        uBrightness: new THREE.Uniform(1.0),
        uSizeScale: new THREE.Uniform(1.0)
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })

    const points = new THREE.Points(geometry, material)
    points.renderOrder = 0 // 天球のすぐ手前、シーンの奥
    points.frustumCulled = false // 位置はシェーダー任せなのでカリングさせない
    points.visible = false
    this.scene.add(points)

    return {
      points,
      material,
      active: false,
      speed: 1
    }
  }

  spawn() {
    const star = this.pool.find(s => !s.active)
    if (!star) return

    const { normal, tangent, helper } = this.scratch

    if (this.hasBurstDir) {
      // バースト中: 指定方向のコーン内（最大 ±35°）に出現させ、カメラに映る空へ集める
      helper.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize()
      tangent.crossVectors(this.burstDir, helper).normalize()
      const coneAngle = Math.random() * 0.6
      normal.copy(this.burstDir).multiplyScalar(Math.cos(coneAngle))
        .addScaledVector(tangent, Math.sin(coneAngle))
        .normalize().multiplyScalar(this.skyRadius)
    } else {
      // 通常: 天球の上半分あたりのランダムな位置
      const theta = Math.random() * Math.PI * 2
      const phi = Math.random() * Math.PI * 0.45 + Math.PI * 0.1 // 高めの空
      normal.setFromSphericalCoords(this.skyRadius, phi, theta)
    }

    // 球面に沿った接線方向（ランダムなベクトルとの外積）
    helper.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize()
    tangent.crossVectors(normal, helper).normalize()
    // 少し下向きに流す
    tangent.y -= 0.35
    tangent.normalize()

    star.material.uniforms.uStart.value.copy(normal)
    star.material.uniforms.uDir.value.copy(tangent)
    star.material.uniforms.uColor.value.copy(this.spawnColor)
    // バースト中は演出としてはっきり見せる（明るく・太く）
    // NOTE: 尾を長くすると trail 32点がまばらになり点線に見えるので長さは変えない
    star.material.uniforms.uBrightness.value = this.burstActive ? 1.8 : 1.0
    star.material.uniforms.uSizeScale.value = this.burstActive ? 1.8 : 1.0
    star.material.uniforms.uProgress.value = 0
    star.material.uniforms.uFlightDist.value = 10 + Math.random() * 8
    star.speed = 0.7 + Math.random() * 0.5 // 1.2〜2秒で流れる
    star.active = true
    star.points.visible = true
  }

  update() {
    if (this.quality.reducedMotion) return

    const deltaSeconds = Math.min(this.time.delta, 100) * 0.001 // タブ復帰などの巨大deltaを抑える

    // 出現タイマー（enabled でない間は進めない）
    if (this.enabled) {
      this.spawnTimer += deltaSeconds
      if (this.spawnTimer >= this.nextSpawnDelay) {
        this.spawnTimer = 0
        this.nextSpawnDelay = this.randomDelay()
        this.spawn()
      }
    }

    // アクティブな流星を進める
    for (const star of this.pool) {
      if (!star.active) continue
      star.material.uniforms.uProgress.value += deltaSeconds * star.speed
      if (star.material.uniforms.uProgress.value >= 1) {
        star.active = false
        star.points.visible = false
        star.material.uniforms.uProgress.value = 0
      }
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled && this.poolSize > 0
  }

  setDebug() {
    if (!this.debug.active || this.poolSize === 0) return

    this.debugFolder = this.debug.ui.addFolder('Shooting Stars')
    this.debugFolder.add(this, 'enabled').name('Enabled').listen()
    this.debugFolder.add({ spawnNow: () => this.spawn() }, 'spawnNow').name('Spawn Now')
    this.debugFolder.close()
  }

  dispose() {
    for (const star of this.pool) {
      this.scene.remove(star.points)
      star.points.geometry.dispose()
      star.material.dispose()
    }
    this.pool.length = 0
  }
}
